"use client";

import { Button } from "@/components/ui/button";
import type { CartItem } from "@/hooks/use-carts";
import { Loader2, Minus, Plus, Trash2 } from "lucide-react";

interface CartItemRowProps {
  item: CartItem;
  cartId: number;
  variant: "editable" | "readOnly";
  onQuantityChange?: (item: CartItem, newQty: number) => void;
  onRemove?: (item: CartItem) => void;
  showLineTotal?: boolean;
  removePending?: boolean;
  /** When true, quantity +/- are disabled and a spinner is shown (e.g. while updating). */
  quantityUpdatePending?: boolean;
  className?: string;
}

export function CartItemRow({
  item,
  cartId,
  variant,
  onQuantityChange,
  onRemove,
  showLineTotal = true,
  removePending = false,
  quantityUpdatePending = false,
  className,
}: CartItemRowProps) {
  const lineTotal = Number(item.itemPrice) * item.quantity;

  if (variant === "readOnly") {
    return (
      <li
        className={
          className ??
          "flex justify-between gap-2 text-xs border-b border-border pb-2 last:border-0"
        }
      >
        <span className="min-w-0 truncate flex-1">{item.itemName}</span>
        <span className="shrink-0">
          {item.quantity} × $ {Number(item.itemPrice).toFixed(2)}
          {showLineTotal ? ` = $ ${lineTotal.toFixed(2)}` : ""}
        </span>
      </li>
    );
  }

  return (
    <div
      className={
        className ??
        "flex flex-wrap items-center justify-between gap-2 py-2 border-b border-border last:border-0"
      }
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{item.itemName}</p>
        <p className="text-xs text-muted-foreground">
          $ {Number(item.itemPrice).toFixed(2)} each
        </p>
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-r-none border-r-0 cursor-pointer"
          onClick={() => onQuantityChange?.(item, Math.max(1, item.quantity - 1))}
          disabled={item.quantity <= 1 || quantityUpdatePending}
          aria-label="Decrease quantity"
          aria-busy={quantityUpdatePending}
        >
          <Minus className="w-4 h-4" />
        </Button>
        <span className="flex h-8 min-w-[2rem] items-center justify-center gap-1 border border-input bg-muted/30 px-2 text-sm font-medium tabular-nums">
          {quantityUpdatePending ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-hidden />
          ) : null}
          <span className={quantityUpdatePending ? "opacity-70" : ""}>{item.quantity}</span>
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-l-none border-l-0 cursor-pointer"
          onClick={() => onQuantityChange?.(item, item.quantity + 1)}
          disabled={quantityUpdatePending}
          aria-label="Increase quantity"
          aria-busy={quantityUpdatePending}
        >
          <Plus className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 cursor-pointer"
          onClick={() => onRemove?.(item)}
          disabled={removePending}
          aria-label="Remove item"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <p className="text-sm font-medium w-20 text-right">
        $ {lineTotal.toFixed(2)}
      </p>
    </div>
  );
}
